import React, {
    PropsWithChildren,
    useCallback,
    useContext,
    useMemo,
} from 'react'
import {
    AllEvents,
    ConfigurationContext,
    DataLayerEventName,
    ElectionDefinition,
    queries,
} from '@news-mono/web-common'
import { ElectionDataFetchError, ElectionTitle } from '../components'
import {
    StyledSwingContainer,
    StyledContentContainer,
    StyledDisclaimerText,
    StyledLargeSwingText,
    StyledChartContainer,
    StyledTextContainer,
} from './TheSwingWidget.styled'
import {
    MeterPieChart,
    MeterPieChartItem,
    MeterPieChartMeter,
} from '../components/MeterPieChart'
import { useQuery } from '@tanstack/react-query'
import { getAreaForElection, partyColors } from '../data'
import { PartyDto } from '@west-australian-newspapers/election-api-types'
import { SwingNeedle } from './assets/SwingNeedle'
import { SwingOverlay } from './assets/SwingOverlay'
import { SwingChartLoading } from './assets/ChartLoading'
import { electionsDebug } from '../../../__helpers/elections-debug'
import { useImpressionAvailable } from '../../../__helpers/impression-available-helper'
export interface TheSwingWidgetProps {
    electionDefinition: ElectionDefinition
    prevElectionYear: number
    onEvent: (event: AllEvents) => void
}
type SwingDataProps = {
    swing: number
    swingToParty?: PartyDto
}
export const TheSwingWidget = ({
    electionDefinition,
    prevElectionYear,
    onEvent,
}: TheSwingWidgetProps) => {
    const config = useContext(ConfigurationContext)
    const { isSuccess, isLoading, data, isError } = useQuery(
        queries['election-api'].definition({
            initialDefinition: electionDefinition,
            electionAPI: config.electionApi,
            caller: config.apiCallerHeader,
        }),
    )

    const swingData: SwingDataProps | undefined = useMemo(() => {
        if (
            !isSuccess ||
            !data.electionData.data ||
            !data.electionData.config
        ) {
            electionsDebug('TheSwingWidget', 'Missing election data or config')
            return
        }
        const swingData = getAreaForElection(
            data.electionData.data,
            data.electionData.config,
        )
        if (!swingData) {
            electionsDebug('TheSwingWidget', 'Missing area data for election')
            return
        }
        // Grab the ALP and COA parties (probably worth storing this in config for future elections)
        const laborParty = swingData.parties.find(
            (party) => party.partyCode === 'ALP',
        )
        const coalitionParty = swingData.parties.find(
            (party) => party.partyCode === 'COA',
        )
        if (!laborParty || !coalitionParty) {
            electionsDebug(
                'TheSwingWidget',
                'Missing party data for ALP or COA Parties',
            )
            return
        }
        // Fallback to 0 state if the TPP data is not available
        if (
            laborParty.tppSwingPct === null ||
            coalitionParty.tppSwingPct === null
        ) {
            electionsDebug(
                'TheSwingWidget',
                'Missing TPP data for ALP or COA Parties',
                laborParty,
                coalitionParty,
            )
            return {
                swingToParty: undefined,
                swing: 0,
            }
        }

        // If the swing towards Labor is negative, then the swing is towards Coalition
        if (laborParty.tppSwingPct < 0) {
            return {
                swingToParty: coalitionParty,
                swing: laborParty.tppSwingPct, // We want to return the negative swing here
            }
        } else if (laborParty.tppSwingPct > 0) {
            // Vice versa
            return {
                swingToParty: laborParty,
                swing: laborParty.tppSwingPct,
            }
        } else {
            // If the swing is zero, fallback to zero state
            return {
                swingToParty: undefined,
                swing: 0,
            }
        }
    }, [data?.electionData.config, data?.electionData.data, isSuccess])

    // Loading state
    if (isLoading) {
        return (
            <SwingWrapper swingPct={0} prevElectionYear={prevElectionYear}>
                <SwingChartLoading />
            </SwingWrapper>
        )
    }

    // Error state
    if (isError || !swingData) {
        return (
            <SwingWrapper
                swingPct={0}
                showText={false}
                prevElectionYear={prevElectionYear}
            >
                <ElectionDataFetchError />
            </SwingWrapper>
        )
    }

    return (
        <SwingWrapper
            swingPct={swingData.swing}
            party={swingData.swingToParty}
            prevElectionYear={prevElectionYear}
            onEvent={onEvent}
        >
            <ChartComponent
                swingPct={swingData.swing}
                partyCode={
                    swingData.swingToParty
                        ? swingData.swingToParty.partyCode
                        : 'ALP'
                }
            />
        </SwingWrapper>
    )
}

type SwingWrapperProps = {
    /** This controls the graph and needle swing direction */
    swingPct: number
    /** When party is undefined, we fallback to the default (zero) state.
     * When presented, this should be the party that the swing is towards. */
    party?: PartyDto
    showText?: boolean
    prevElectionYear: number
    onEvent?: (event: AllEvents) => void
}
const SwingWrapper = ({
    swingPct,
    party,
    children,
    showText = true,
    prevElectionYear,
    onEvent,
}: PropsWithChildren<SwingWrapperProps>) => {
    const impressionAvailableRef = useImpressionAvailable({
        loading: false,
        available: useCallback(() => {
            onEvent?.({
                type: DataLayerEventName.navAvailable,
                originator: 'ElectionSwingComponent',
                payload: {
                    navName: 'ElectionSwingComponent',
                },
            })
        }, [onEvent]),
    })

    const swingText =
        swingPct === 0
            ? 'either party'
            : party
            ? getPartyName(party)
            : 'either party'
    return (
        <StyledSwingContainer ref={impressionAvailableRef}>
            <ElectionTitle>The Swing</ElectionTitle>
            <StyledContentContainer>
                {showText && (
                    <StyledTextContainer>
                        <StyledLargeSwingText
                            textColor={
                                party
                                    ? partyColors[party.partyCode].primary
                                    : undefined
                            }
                        >
                            <strong>
                                {Math.round(Math.abs(swingPct) * 10) / 10}%
                            </strong>
                            <span>
                                toward&nbsp;
                                {swingText}
                            </span>
                        </StyledLargeSwingText>
                        <StyledDisclaimerText type="desktop">
                            Percentage based on two-party preferred results
                            compared with {prevElectionYear}&nbsp;election.
                        </StyledDisclaimerText>
                    </StyledTextContainer>
                )}
                {children}
                <StyledDisclaimerText type="mobile">
                    Percentage based on two-party preferred results compared
                    with {prevElectionYear}&nbsp;election.
                </StyledDisclaimerText>
            </StyledContentContainer>
        </StyledSwingContainer>
    )
}

const ChartComponent = ({
    swingPct,
    partyCode,
}: {
    swingPct: number
    partyCode: string
}) => {
    const MIN_SWING = -15
    const MAX_SWING = 15

    let start, end
    if (swingPct > 0) {
        start = 0
        end = swingPct
    } else if (swingPct < 0) {
        start = swingPct
        end = 0
    }

    return (
        <StyledChartContainer>
            <MeterPieChart
                min={MIN_SWING}
                max={MAX_SWING}
                height="100%"
                innerCutoutRatio={0.35}
                items={
                    <>
                        <MeterPieChartItem
                            start={MIN_SWING}
                            end={0}
                            fill={partyColors['LIB'].light}
                        />
                        {/* This item represents the overlay highlighting the swing towards a particular party */}
                        {start !== undefined && end !== undefined && (
                            <MeterPieChartItem
                                start={start}
                                end={end}
                                fill={partyColors[partyCode].primary}
                            />
                        )}
                        <MeterPieChartItem
                            start={Math.max(0, end ?? 0)} // Setting this to zero causes the middle pie chart to not display when swinging right ¯\_(ツ)_/¯
                            end={MAX_SWING}
                            fill={partyColors['ALP'].light}
                        />
                    </>
                }
            >
                <MeterPieChartMeter value={swingPct}>
                    <SwingNeedle />
                </MeterPieChartMeter>
                <SwingOverlay />
            </MeterPieChart>
        </StyledChartContainer>
    )
}

/** Returns shortPartyName if available, then falls back to partyName, finally falls back to partyCode */
const getPartyName = (party: PartyDto) => {
    return party.shortPartyName ?? party.partyName ?? party.partyCode
}
